// ─────────────────────────────────────────────────────────────────────────────
// Service de prévision (Flask, blueprint /prevision-f) — via aiClient.
// Les réponses suivent l'enveloppe { success, data?, error? }.
// ─────────────────────────────────────────────────────────────────────────────
import { aiClient } from "../../shared/api/clients";
import { getErrorMessage } from "../../shared/api/http";

export interface ForecastEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const getEnvelope = <T>(path: string, params?: Record<string, string | number | undefined>) =>
  aiClient.get<ForecastEnvelope<T>>(path, { params }).then(({ data }) => data);

// ── Référentiels ─────────────────────────────────────────────────────────────

export interface FamilleParentDto {
  codFamp: string;
  designation: string | null;
  groupe?: string | null;
}

export interface SousFamilleDto {
  codFam: string;
  desgFam: string | null;
  codFamp: string | null;
  desgFamp?: string | null;
}

export interface ForecastDepotDto {
  codDep: string;
  libelle: string;
  /** Variantes renvoyées selon la route */
  code_dep?: string;
  cod_dep?: string;
}

export interface ForecastVendeurDto {
  [key: string]: unknown;
}

export interface HistoriqueStep {
  date: string;
  ca_reel: number;
}

export const getForecastFamilles = <T = FamilleParentDto>() =>
  getEnvelope<T[]>("/prevision-f/familles");

export const getForecastSousFamilles = <T = SousFamilleDto>(famille: string) =>
  getEnvelope<T[]>("/prevision-f/sous-familles-list", { famille });

export const getForecastToutesSousFamilles = <T = SousFamilleDto>() =>
  getEnvelope<T[]>("/prevision-f/toutes-sous-familles");

export const getForecastDepots = <T = ForecastDepotDto>() =>
  getEnvelope<T[]>("/prevision-f/depots");

export const getForecastDepotsFiltered = <T = ForecastDepotDto>(
  famille: string,
  sousFamille: string,
) =>
  getEnvelope<T[]>("/prevision-f/depots-filtered", {
    famille,
    sous_famille: sousFamille,
  });

export const getForecastVendeurs = <T = ForecastVendeurDto>(
  depot: string,
  famille: string,
  sousFamille: string,
) =>
  getEnvelope<T[]>("/prevision-f/vendeurs-by-depot", {
    depot,
    famille,
    sous_famille: sousFamille,
  });

export interface HistoriqueRequest {
  famille: string;
  sousFamille: string;
  depot: string;
  n?: number;
  vendeur?: string;
}

export const getForecastHistorique = ({
  famille,
  sousFamille,
  depot,
  n = 12,
  vendeur,
}: HistoriqueRequest) =>
  getEnvelope<HistoriqueStep[]>("/prevision-f/historique-semaines", {
    famille,
    sous_famille: sousFamille,
    depot,
    n,
    vendeur: vendeur || undefined,
  });

// ── Prévision par horizon (LightGBM) ─────────────────────────────────────────

export interface PrevisionHorizonRequest {
  famille: string;
  sous_famille: string;
  depot: string | null;
  horizon: number;
  vendeur?: string;
}

export interface PrevisionHorizonStep {
  step: number;
  date: string;
  ca_predit: number;
  borne_basse: number;
  borne_haute: number;
}

export interface PrevisionHorizonResponse {
  success: boolean;
  famille?: string;
  sous_famille?: string;
  depot?: string;
  vendeur?: string;
  horizon?: number;
  ca_total?: number;
  date_from?: string;
  date_to?: string;
  previsions?: PrevisionHorizonStep[];
  error?: string;
}

/** Lève une ApiError si le service répond en erreur HTTP. */
export const postPrevisionHorizon = <T = PrevisionHorizonResponse>(
  req: PrevisionHorizonRequest,
) =>
  aiClient
    .post<T>("/prevision-f/prevision-horizon", req)
    .then(({ data }) => data);

/**
 * Variante qui ne lève jamais : une erreur (HTTP ou réseau) est renvoyée sous
 * la forme { success: false, error } avec le message du serveur, pour les
 * écrans qui traitent l'échec comme un état de ligne ("aucune donnée"…).
 */
export const tryPrevisionHorizon = (
  req: PrevisionHorizonRequest,
): Promise<PrevisionHorizonResponse> =>
  postPrevisionHorizon(req).catch((err) => ({
    success: false,
    error: getErrorMessage(err, "Erreur"),
  }));

// ── Ancienne route /prevision-f/prevision ────────────────────────────────────

export interface PrevisionFRequest {
  dimension: "famille" | "sousfamille";
  labelCode: string;
  dateFrom: string;
  dateTo: string;
  compareYears?: number;
  depot?: string | null;
}

/** Snapshot annuel du même mois (same_month_history) */
export interface SameMonthSnapshot {
  year: number;
  month: number;
  caTotal: number;
  nbJours: number;
  caMoyJour: number;
}

/** Snapshot annuel global (history) */
export interface AnnualSnapshotDto {
  year: number;
  totalCa: number;
  totalQty: number;
  nbJours: number;
  moyJour: number;
}

export interface ModelMetrics {
  mae: number;
  rmse: number;
  r2: number;
  mape: number;
  mdape: number;
  n: number;
}

export interface WeekRecord {
  week: "S-1" | "S-2";
  dateFrom: string;
  dateTo: string;
  caTotal: number;
  nbJours: number;
}

export interface PrevisionFResponse {
  success: boolean;
  dimension: string;
  labelCode: string;
  labelDesignation: string;
  dateFrom: string;
  dateTo: string;
  depot: string | null;
  forecastCa: number;
  forecastQty: number;
  forecastDays: number;
  forecastMoyJour: number;
  yoyGrowthPct: number;
  history: AnnualSnapshotDto[];
  sameMonthHistory?: SameMonthSnapshot[];
  weeklyHistory?: WeekRecord[];
  predictions: {
    date: string;
    predicted_ca: number;
    lower: number;
    upper: number;
    month_total: number;
  }[];
  monthly_results: {
    year: number;
    month: number;
    year_month: string;
    ca_prevu_total: number;
    ca_prevu_periode: number;
    n_days_total: number;
    n_days_asked: number;
    xgb_correction: number;
  }[];
  meta: {
    n_train: number | null;
    n_test: number | null;
    n_val: number | null;
    train_metrics: Partial<ModelMetrics>;
    test_metrics: Partial<ModelMetrics>;
    val_metrics: Partial<ModelMetrics>;
    trend_growth_pct: number | null;
    best_iteration: number | null;
    cv_r2_mean: number | null;
    xgb_enabled?: boolean | null;
  };
  error?: string;
}

/** @deprecated Préférer getForecastFamilles (réponse en enveloppe typée). */
export const getPrevisionFamilles = () =>
  aiClient.get<FamilleParentDto[]>("/prevision-f/familles");

/** @deprecated Préférer getForecastDepots. */
export const getPrevisionFDepots = () =>
  aiClient.get<{ depots: { cod_dep: string; nb_lignes: number }[] }>(
    "/prevision-f/depots",
  );

/** @deprecated Préférer getForecastSousFamilles. */
export const getPrevisionSousFamilles = (codFamp?: string) =>
  aiClient.get<SousFamilleDto[]>("/prevision-f/sousfamilles", {
    params: { codFamp: codFamp || undefined },
  });

export const postPrevisionF = (req: PrevisionFRequest) =>
  aiClient.post<PrevisionFResponse>("/prevision-f/prevision", req);
